import { isDefined } from '@nfctron/nexus-core';

import { type TrackingTicketOptions } from '@root/lib/modules/tracking/tracking.module';

export interface GtagJsAdapterRegisterConfig {
	/** gtag.js tracking ID */
	trackingId: string;
	/** Conversion labels for specific events */
	conversionLabels?: Partial<Record<ConversionLabelEvent, string>>;
}

/**
 * Gtag.js tracking adapter
 * @see https://developers.google.com/tag-platform/gtagjs
 * @author filipditrich <ditrich@nfctron.com>
 */
export class GtagJsAdapter {
	private readonly allowTracking;
	public gtagTrackingIds: string[] = [];
	public conversionLabels: ConversionLabelsConfig = {
		addToCart: [],
		purchase: [],
		removeFromCart: [],
		beginCheckout: [],
	};

	get gtag(): Gtag.Gtag | undefined {
		if (typeof window === 'undefined') return undefined;
		return (window as any)?.gtag as Gtag.Gtag | undefined;
	}

	constructor(allowTracking: boolean = false) {
		this.allowTracking = allowTracking;
	}

	/**
	 * Registers gtag.js tracking ID
	 * @param trackingId gtag.js tracking ID
	 * @param conversionLabels Conversion labels
	 */
	public register = ({ trackingId, conversionLabels }: GtagJsAdapterRegisterConfig) => {
		/** ignore when tracking is disallowed */
		if (!this.allowTracking) return;
		/** ignore when duplicate trackingId is passed */
		if (this.gtagTrackingIds.includes(trackingId)) return;
		/** create script if it does not exist yet */
		const scriptId = `ga-gtag`;
		console.debug('📈 [GtagJsAdapter] register(%s) called', trackingId);
		const scriptEl = document.getElementById(scriptId);
		if (!isDefined(scriptEl)) {
			/** construct the script */
			const script = document.createElement('script');
			script.id = scriptId;
			script.type = 'text/javascript';
			script.async = true;
			script.src = `https://www.googletagmanager.com/gtag/js`;

			/** create dataLayer if it does not exist yet */
			(window as any).dataLayer = (window as any).dataLayer || [];
			/** register the gtag method */
			(window as any).gtag =
				(window as any).gtag ||
				function gtag() {
					// eslint-disable-next-line prefer-rest-params
					(window as any).dataLayer?.push(arguments);
				};

			/** insert the script to the top of <head> */
			document.head.insertBefore(script, document.head.firstChild);
		}

		/** call the gtag method to register the trackingId */
		this.gtagTrackingIds.push(trackingId);
		/** set conversion labels */
		if (isDefined(conversionLabels?.addToCart)) this.conversionLabels.addToCart.push(`${trackingId}/${conversionLabels.addToCart}`);
		if (isDefined(conversionLabels?.purchase)) this.conversionLabels.purchase.push(`${trackingId}/${conversionLabels.purchase}`);
		if (isDefined(conversionLabels?.removeFromCart)) this.conversionLabels.removeFromCart.push(`${trackingId}/${conversionLabels.removeFromCart}`);
		if (isDefined(conversionLabels?.beginCheckout)) this.conversionLabels.beginCheckout.push(`${trackingId}/${conversionLabels.beginCheckout}`);
		/** register the gtag trackingId */
		this.gtag?.('js', new Date());
		this.gtag?.('config', trackingId);
	};

	/**
	 * Event handler function
	 * @see https://developers.google.com/tag-platform/gtagjs/reference/events
	 * @param name Event name
	 * @param options Event options
	 * @param ref Additional reference data
	 */
	public handleEvent = (
		name: Gtag.EventNames,
		options: Gtag.ControlParams | Gtag.EventParams | Gtag.CustomParams = {},
		ref: Record<string, unknown> = {},
	) => {
		if (!this.allowTracking || this.gtagTrackingIds.length === 0 || !isDefined(this.gtag)) return;
		console.debug('📈 [GtagJsAdapter] handleEvent(%s) called', name, options, ref);
		this.gtag?.('event', name, { ...options, ...ref });
	};

	/**
	 * Page view handler function
	 * @see https://developers.google.com/tag-platform/gtagjs/reference
	 * @param url Page URL
	 * @param options Page options
	 */
	public handlePageView = (url: string, options?: Gtag.ControlParams | Gtag.EventParams | Gtag.ConfigParams | Gtag.CustomParams) => {
		if (!this.allowTracking || this.gtagTrackingIds.length === 0) return;
		console.debug('📈 [GtagJsAdapter] handlePageView(%s) called', url);
		this.gtag?.('set', { page_location: url, ...options });
	};

	/**
	 * Converts tickets list to gtag.js Item structure
	 * @param tickets List of tickets
	 * @returns List of gtag items
	 */
	public static convertTicketsToItems = (tickets: Array<TrackingTicketOptions>) => {
		const eventGroup = tickets.flatMap((ticket) => ticket.trackingContext?.eventGroups.map((group) => group.namePub)).join(', ');
		return tickets.reduce((acc, ticket) => {
			const foundIndex = acc.findIndex((item) => item.item_id === ticket.ticketType.ticketTypeId);

			if (foundIndex >= 0) {
				acc[foundIndex]!.quantity += 1;
			} else {
				/** add new ticket item */
				acc.push({
					index: ticket.ticketType.displayOrder,
					item_id: ticket.ticketType.ticketTypeId,
					item_name: ticket.ticketType.namePub,
					item_category: 'ticket',
					item_category2: eventGroup || ticket.trackingContext?.eventName,
					item_category3: ticket.trackingContext?.eventLocation || ticket.trackingContext?.eventPlace,
					item_category4: ticket.ticketType.ticketTypeGroup?.namePub || ticket.ticketType.namePub,
					price: ticket.ticketType.price / 100,
					quantity: 1,
				});
			}

			/** add chip items */
			if (ticket.chips.length > 0) {
				const ticketChips = ticket.chips.map((chip, index) => ({
					index,
					item_id: chip.productItemId,
					item_name: 'Voucher',
					item_category: 'chip',
					item_category2: eventGroup || ticket.trackingContext?.eventName,
					item_category3: ticket.trackingContext?.eventLocation || ticket.trackingContext?.eventPlace,
					item_category4: ticket.ticketType.ticketTypeGroup?.namePub || ticket.ticketType.namePub,
					price: chip.amount / 100,
					quantity: 1,
				}));
				acc.push(...ticketChips);
			}
			/** add extra product items */
			if (ticket.extraProducts.length > 0) {
				const ticketExtraProducts = ticket.extraProducts.reduce((accExtra, extraProduct, index) => {
					const foundIndexExtra = accExtra.findIndex((item) => item.item_id === extraProduct.ticketProductId);

					if (foundIndexExtra >= 0) {
						accExtra[foundIndexExtra]!.quantity += extraProduct.count;
					} else {
						accExtra.push({
							item_id: extraProduct.ticketProductId,
							item_name: extraProduct.name,
							index: extraProduct.displayOrder,
							item_category: 'extra_product',
							item_category2: eventGroup || ticket.trackingContext?.eventName,
							item_category3: ticket.trackingContext?.eventLocation || ticket.trackingContext?.eventPlace,
							item_category4: ticket.ticketType.ticketTypeGroup?.namePub || ticket.ticketType.namePub,
							price: extraProduct.price / 100,
							quantity: extraProduct.count,
						});
					}

					/** add extra product chip items */
					if (extraProduct.chips.length > 0) {
						const extraProductChips = extraProduct.chips.map((chip, indexExtra) => ({
							index: indexExtra,
							item_id: chip.productItemId,
							item_name: 'Voucher',
							item_category: 'chip',
							item_category2: eventGroup || ticket.trackingContext?.eventName,
							item_category3: ticket.trackingContext?.eventLocation || ticket.trackingContext?.eventPlace,
							item_category4: ticket.ticketType.ticketTypeGroup?.namePub || ticket.ticketType.namePub,
							price: chip.amount / 100,
							quantity: 1,
						}));
						acc.push(...extraProductChips);
					}

					return accExtra;
				}, [] as Gtag.Item[]);
				acc.push(...ticketExtraProducts);
			}
			return acc;
		}, [] as Array<Gtag.Item>);
	};

	/**
	 * Conversion event handler
	 * @param label Conversion label
	 * @param params Conversion parameters
	 * @param ref Additional reference data
	 */
	public conversion = (
		label: string,
		params: {
			value: number;
			currency: string;
			transaction_id?: string;
		},
		ref: Record<string, unknown> = {},
	) => {
		this.gtag?.('event', 'conversion', {
			send_to: label,
			value: params.value,
			currency: params.currency,
			transaction_id: params.transaction_id ?? ref.orderId ?? ref.eventId,
			...ref,
		});
	};

	/**
	 * BeginCheckout event handler
	 * @param params InitiateCheckout parameters
	 * @param ref Additional reference data
	 */
	public beginCheckout = (params: Gtag.BeginCheckoutParams, ref: Record<string, unknown> = {}) => {
		this.handleEvent('begin_checkout', params, ref);
		this.conversionLabels.beginCheckout.forEach((label) => this.conversion(label, params, ref));
	};

	/**
	 * Purchase event handler
	 * @param params Purchase parameters
	 * @param ref Additional reference data
	 */
	public purchase = (params: Gtag.PurchaseParams, ref: Record<string, unknown> = {}) => {
		this.handleEvent('purchase', params, ref);
		this.conversionLabels.purchase.forEach((label) => this.conversion(label, params, ref));
	};

	/**
	 * AddToCart event handler
	 * @param params AddToCart parameters
	 * @param ref Additional reference data
	 */
	public addToCart = (params: Gtag.AddToCartParams, ref: Record<string, unknown> = {}) => {
		this.handleEvent('add_to_cart', params, ref);
		this.conversionLabels.addToCart.forEach((label) => this.conversion(label, params, ref));
	};

	/**
	 * RemoveFromCart event handler
	 * @param params RemoveFromCart parameters
	 * @param ref Additional reference data
	 */
	public removeFromCart = (params: Gtag.RemoveFromCartParams, ref: Record<string, unknown> = {}) => {
		this.handleEvent('remove_from_cart', params, ref);
		this.conversionLabels.removeFromCart.forEach((label) => this.conversion(label, params, ref));
	};
}

type ConversionLabelEvent = 'addToCart' | 'purchase' | 'removeFromCart' | 'beginCheckout';
type ConversionLabelsConfig = Record<ConversionLabelEvent, string[]>;
